import React, { FC, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type TeamPursuitSprintTableProps = {
    values: string[][];
};

const validQValues = ['Q', 'QG', 'QB', 'Q5'];
const isQualified = (value?: string) => value ? validQValues.includes(value.toUpperCase().trim()) : false;
const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';

export const TeamPursuitSprintTable: FC<TeamPursuitSprintTableProps> = ({ values }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const blocks = useMemo(() => {
        if (!values || values.length === 0) return [];

        let isBlockFormat = false;
        let globalHeader: string[] | null = null;

        // 1. PRVNÍ PRŮCHOD: Najdeme hlavičku a zjistíme formát (Finále / Round 1 s Heaty)
        for (const row of values) {
            const rowStr = row.join(' ').toLowerCase();
            
            // Detekce bloků: Obsahuje "Final for" NEBO začíná slovem "Heat" a číslem (např. Heat 1)
            if (rowStr.includes('final for') || row.some(c => /^heat\s*\d+/i.test(String(c).trim()))) {
                isBlockFormat = true;
            }

            // Najdeme univerzální hlavičku tabulky (musí mít Bib a Jméno/Zemi)
            if (!globalHeader && rowStr.includes('bib') && (rowStr.includes('name') || rowStr.includes('country'))) {
                globalHeader = row;
            }
        }

        // ==========================================
        // KVALIFIKACE (JEDNA SOUVISLÁ TABULKA)
        // ==========================================
        if (!isBlockFormat) {
            const startIdx = globalHeader ? values.findIndex(r => r === globalHeader) : 0;
            return [{
                title: '',
                data: values.slice(startIdx !== -1 ? startIdx : 0)
            }];
        }

        // ==========================================
        // FINÁLE A ROUND 1 (ROZDĚLENÍ NA BLOKY)
        // ==========================================
        const result: { title: string; data: string[][] }[] = [];
        let currentBlock: { title: string; data: string[][] } | null = null;

        for (const row of values) {
            const rowStr = row.join(' ').toLowerCase();
            const isHeaderMatch = rowStr.includes('final for') || row.some(c => /^heat\s*\d+/i.test(String(c).trim()));

            if (isHeaderMatch) {
                // Uložíme předchozí blok
                if (currentBlock && currentBlock.data.length > 0) result.push(currentBlock);

                // Získáme přesný název z buňky ("Heat 1", "Final for BRONZE")
                const textCells = row.filter(c => c && String(c).trim() !== '');
                const titleMatches = textCells.filter(c => {
                    const s = String(c).trim().toLowerCase();
                    return s.includes('final for') || /^heat\s*\d+/i.test(s);
                });
                const titleCell = titleMatches.length > 0 ? titleMatches[titleMatches.length - 1] : textCells[textCells.length - 1];

                currentBlock = { title: String(titleCell).trim(), data: [] };

                // 🎯 INJEKCE HLAVIČKY: Vložíme globální hlavičku hned na první řádek nového bloku
                if (globalHeader) {
                    currentBlock.data.push(globalHeader);
                }
            } else if (currentBlock) {
                // Pokud narazíme na tu samou hlavičku fyzicky v datech pod nadpisem, přeskočíme ji (abychom ji neměli 2x)
                if (globalHeader && row.join() === globalHeader.join()) {
                    continue;
                }
                currentBlock.data.push(row);
            }
        }
        
        if (currentBlock && currentBlock.data.length > 0) result.push(currentBlock);
        return result;
    }, [values]);

    if (blocks.length === 0) return null;

    return (
        <Box>
            {blocks.map((block, idx) => (
                <TeamBlock key={idx} title={block.title} data={block.data} isMobile={isMobile} />
            ))}
        </Box>
    );
};

// =========================================================
// SUB-KOMPONENTA PRO VYKRESLENÍ JEDNOHO BLOKU/TABULKY
// =========================================================
const TeamBlock = ({ title, data, isMobile }: { title?: string, data: string[][], isMobile: boolean }) => {
    
    const firstDataRowIdx = data.findIndex(row => row.some(c => c && String(c).trim() !== ''));
    const validData = firstDataRowIdx !== -1;

    const headers = validData ? data[firstDataRowIdx] : [];
    const rows = validData ? data.slice(firstDataRowIdx + 1) : [];

    const countryColIndex = headers.findIndex(h => h && h.toLowerCase().includes('country'));
    const qColIndex = headers.findIndex(h => h && h.toLowerCase() === 'q');
    const timeColIndex = headers.findIndex(h => h && h.toLowerCase().includes('time'));
    
    const teamBibIndex = headers.findIndex(h => h && h.toLowerCase().includes('team bib'));
    const individualBibIndex = headers.findIndex(h => h && h.toLowerCase() === 'bib');
    const lastNameIdx = headers.findIndex(h => h && h.toLowerCase().includes('last'));
    const firstNameIdx = headers.findIndex(h => h && h.toLowerCase().includes('first'));

    const groupColumnIndex = teamBibIndex !== -1 ? teamBibIndex : 0;

    const teams = useMemo(() => {
        if (!validData || rows.length === 0) return [];
        const result: string[][][] = [];
        let current: string[][] = [];

        rows.forEach((row) => {
            // 1. Prázdný oddělovací řádek
            const isEmpty = row.every(c => !c || String(c).trim() === '');
            if (isEmpty) {
                if (current.length > 0) result.push(current);
                current = [];
                return;
            }

            // 2. Detekce "duchů" (řádky ze sloučených buněk)
            const indBib = individualBibIndex !== -1 ? row[individualBibIndex] : '';
            const lastName = lastNameIdx !== -1 ? row[lastNameIdx] : '';
            const columnsExist = individualBibIndex !== -1 || lastNameIdx !== -1;
            
            const hasRider = !columnsExist || (indBib && String(indBib).trim() !== '') || (lastName && String(lastName).trim() !== '');

            if (!hasRider) return; // Ignorujeme

            // 3. Seskupení
            const currentGroupVal = row[groupColumnIndex];
            const prevGroupVal = current.length > 0 ? current[current.length - 1][groupColumnIndex] : null;

            if (current.length > 0 && currentGroupVal && prevGroupVal && currentGroupVal !== prevGroupVal) {
                result.push(current);
                current = [];
            }

            current.push(row);
        });
        
        if (current.length > 0) result.push(current);
        return result;
    }, [rows, groupColumnIndex, validData, individualBibIndex, lastNameIdx]);

    const mergeIndices = useMemo(() => {
        const indices = [0]; 
        if (teamBibIndex !== -1) indices.push(teamBibIndex);
        if (timeColIndex !== -1) indices.push(timeColIndex);
        if (qColIndex !== -1) indices.push(qColIndex);
        
        const avgSpeedIdx = headers.findIndex(h => h && h.toLowerCase().includes('avg speed'));
        if (avgSpeedIdx !== -1) indices.push(avgSpeedIdx);
        
        return indices;
    }, [teamBibIndex, timeColIndex, qColIndex, headers]);

    if (!validData || teams.length === 0) return null;

    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2, mb: 4 }}>
                {title && (
                     <Paper sx={{ backgroundColor: 'grey.900', color: 'common.white', p: 1.5, borderRadius: 2 }}>
                        <Typography fontWeight={700} align="center" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                            {title}
                        </Typography>
                    </Paper>
                )}
                
                {teams.map((team, index) => {
                    const firstRider = team[0];
                    if (!firstRider) return null;

                    const rank = firstRider[0] || '';
                    const time = timeColIndex !== -1 ? firstRider[timeColIndex] : '';
                    const q = qColIndex !== -1 ? firstRider[qColIndex] : '';

                    return (
                        <Paper key={index} sx={{ p: 2, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
                                    {rank && (
                                        <Typography fontWeight={700} sx={{ fontSize: '0.9rem', textAlign: 'center' }}>
                                            {isAbandoned(rank) ? '' : (rank.match(/^\d+$/) ? `#${rank}` : rank)}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {team.map((rider, rIdx) => {
                                        const country = countryColIndex !== -1 ? rider[countryColIndex] : '';
                                        const indBib = individualBibIndex !== -1 ? rider[individualBibIndex] : '';
                                        const lastName = lastNameIdx !== -1 ? rider[lastNameIdx] : '';
                                        const firstName = firstNameIdx !== -1 ? rider[firstNameIdx] : '';

                                        return (
                                            <Box key={rIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Typography fontWeight={700} sx={{ minWidth: 28, textAlign: 'right', fontSize: '0.9rem' }}>
                                                    {indBib}
                                                </Typography>
                                                
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>{lastName}</Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.1 }}>{firstName}</Typography>
                                                </Box>
                                                
                                                {country && (
                                                    <img src={getFlagUrl(country)} alt={country} style={{ width: '22px', height: '15px', objectFit: 'cover', borderRadius: '2px' }} />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Box sx={{ minWidth: 65, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 0.5 }}>
                                    {time && <Typography fontWeight={700} variant="body2">{time}</Typography>}
                                    {isQualified(q) && <span className="qualified-badge">{q.toUpperCase().trim()}</span>}
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 4 }}>
            {title && (
                <Paper sx={{ backgroundColor: 'grey.900', color: 'common.white', p: 1.5, mb: 0, borderRadius: '8px 8px 0 0' }}>
                    <Typography fontWeight={700} align="center" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                        {title}
                    </Typography>
                </Paper>
            )}
            <TableContainer component={Paper} sx={{ borderRadius: title ? '0 0 8px 8px' : 2, boxShadow: 3, overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((header, index) => {
                                if (index === teamBibIndex) return null;
                                return (
                                    <TableCell
                                        key={index}
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            backgroundColor: 'grey.900',
                                            color: 'common.white',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teams.map((team, teamIndex) =>
                            team.map((row, rowIndex) => {
                                const isLastInTeam = rowIndex === team.length - 1;
                                const isLastTeamInTable = teamIndex === teams.length - 1;

                                const currentBorder = isLastInTeam && !isLastTeamInTable
                                    ? '3px solid rgba(0, 0, 0, 0.87)' 
                                    : '1px solid rgba(224, 224, 224, 1)';

                                return (
                                    <TableRow key={`${teamIndex}-${rowIndex}`}>
                                        {headers.map((_, cellIndex) => {
                                            if (cellIndex === teamBibIndex) return null;

                                            const cellValue = row[cellIndex];
                                            const abandoned = isAbandoned(cellValue);
                                            const qualified = cellIndex === qColIndex && isQualified(cellValue);
                                            const isMerged = mergeIndices.includes(cellIndex);

                                            if (isMerged && rowIndex > 0) return null;

                                            return (
                                                <TableCell
                                                    key={cellIndex}
                                                    align="center"
                                                    rowSpan={isMerged ? team.length : 1}
                                                    sx={{
                                                        verticalAlign: 'middle',
                                                        fontWeight: cellIndex === 0 || abandoned ? 700 : 400,
                                                        borderBottom: isMerged 
                                                            ? (!isLastTeamInTable ? '3px solid rgba(0, 0, 0, 0.87)' : '1px solid rgba(224, 224, 224, 1)')
                                                            : currentBorder
                                                    }}
                                                >
                                                    {qualified ? (
                                                        <span className="qualified-badge">{cellValue.toUpperCase().trim()}</span>
                                                    ) : cellIndex === countryColIndex && cellValue ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <img src={getFlagUrl(cellValue)} alt={cellValue} style={{ width: '24px' }} />
                                                        </Box>
                                                    ) : (
                                                        cellValue ?? '—'
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}